/* eslint-disable max-len */
/* src/components/Footer/Footer.jsx */
import { motion } from 'framer-motion';
import {
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaLinkedin, FaFacebook, FaDownload
} from 'react-icons/fa';

// Définition des couleurs modernes
const colors = {
  primary: '#3a86ff',
  secondary: '#ff006e',
  accent: '#8338ec',
  dark: '#1e2a3a',
  darkBlue: '#0f172a',
  light: '#fcfcfc',
  glass: 'rgba(255, 255, 255, 0.1)',
  glassDark: 'rgba(14, 25, 45, 0.9)',
};

const footerStyle = {
  position: 'relative',
  background: colors.darkBlue,
  color: colors.light,
  padding: '70px 0 30px',
  textAlign: 'center',
  fontSize: '1rem',
  overflow: 'hidden',
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
};

const containerStyle = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 20px',
  position: 'relative',
  zIndex: 5,
};

const linkStyle = {
  color: colors.light,
  textDecoration: 'none',
  margin: '0 15px',
  fontSize: '0.95rem',
  fontWeight: '500',
  padding: '8px 15px',
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  display: 'inline-block',
  position: 'relative',
  letterSpacing: '0.3px',
};

const socialIconStyle = {
  color: colors.light,
  width: '20px',
  height: '20px',
  margin: '0 15px',
  transition: 'all 0.3s ease',
};

const footerInfoStyle = {
  marginTop: '40px',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '40px',
};

const footerColumnStyle = {
  flex: '1 1 250px',
  textAlign: 'left',
  minWidth: '200px',
  maxWidth: '300px',
};

const footerHeadingStyle = {
  fontSize: '1.2rem',
  fontWeight: '600',
  marginBottom: '20px',
  position: 'relative',
  paddingBottom: '10px',
  color: colors.light,
};

const contactItemStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '15px',
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '0.95rem',
};

const iconStyle = {
  marginRight: '10px',
  color: colors.primary,
};

function Footer() {
  return (
    <footer style={footerStyle}>
      {/* Effets de background */}
      <div style={{
        position: 'absolute',
        top: '-100px',
        left: '-100px',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: `radial-gradient(circle, ${colors.primary}15, transparent 70%)`,
        filter: 'blur(50px)',
        opacity: 0.8,
        zIndex: 0,
      }}
      />

      <div style={{
        position: 'absolute',
        bottom: '-50px',
        right: '-50px',
        width: '250px',
        height: '250px',
        borderRadius: '50%',
        background: `radial-gradient(circle, ${colors.secondary}20, transparent 70%)`,
        filter: 'blur(50px)',
        opacity: 0.6,
        zIndex: 0,
      }}
      />

      <div style={containerStyle}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '30px',
          }}
          >
            <motion.a
              href="https://facebook.com/drivesoft"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, color: colors.primary }}
              style={socialIconStyle}
            >
              <FaFacebook size={22} />
            </motion.a>
            <motion.a
              href="https://linkedin.com/company/drivesoft"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.2, color: colors.primary }}
              style={socialIconStyle}
            >
              <FaLinkedin size={22} />
            </motion.a>
          </div>
        </motion.div>

        <motion.div
          style={footerInfoStyle}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <div style={footerColumnStyle}>
            <h3 style={footerHeadingStyle}>
              À propos
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '40px',
                height: '3px',
                background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                borderRadius: '2px',
              }}
              />
            </h3>
            <p style={{ fontSize: '0.95rem', lineHeight: '1.7', color: 'rgba(255, 255, 255, 0.8)' }}>
              Drivesoft est une solution innovante qui révolutionne la gestion du transport sanitaire avec une technologie de pointe.
            </p>
          </div>

          <div style={footerColumnStyle}>
            <h3 style={footerHeadingStyle}>
              Contact
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '40px',
                height: '3px',
                background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                borderRadius: '2px',
              }}
              />
            </h3>
            <div style={contactItemStyle}>
              <FaMapMarkerAlt style={iconStyle} />
              <span>3 rue Marlène Dietrich, Herblay-sur-Seine</span>
            </div>
            <div style={contactItemStyle}>
              <FaPhone style={iconStyle} />
              <span>06 52 80 77 36</span>
            </div>
            <div style={contactItemStyle}>
              <FaEnvelope style={iconStyle} />
              <span>contact@drivesoft.fr</span>
            </div>
          </div>

          <div style={footerColumnStyle}>
            <h3 style={footerHeadingStyle}>
              Liens utiles
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '40px',
                height: '3px',
                background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                borderRadius: '2px',
              }}
              />
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ marginBottom: '12px' }}>
                <motion.a
                  href="/qui-sommes-nous"
                  style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none', fontSize: '0.95rem' }}
                  whileHover={{ color: colors.primary, x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Qui sommes-nous
                </motion.a>
              </li>
              <li style={{ marginBottom: '12px' }}>
                <motion.a
                  href="/tarifs"
                  style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none', fontSize: '0.95rem' }}
                  whileHover={{ color: colors.primary, x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Tarifs
                </motion.a>
              </li>
              <li style={{ marginBottom: '12px' }}>
                <motion.a
                  href="/actualites"
                  style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none', fontSize: '0.95rem' }}
                  whileHover={{ color: colors.primary, x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Actualités
                </motion.a>
              </li>
              <li style={{ marginBottom: '12px' }}>
                <motion.a
                  href="/recrutement"
                  style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none', fontSize: '0.95rem' }}
                  whileHover={{ color: colors.primary, x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Recrutement
                </motion.a>
              </li>
              <li style={{ marginBottom: '12px' }}>
                <motion.a
                  href="/admin/login"
                  style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none', fontSize: '0.95rem' }}
                  whileHover={{ color: colors.primary, x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Connexion administrateur
                </motion.a>
              </li>
              <li style={{ marginBottom: '12px' }}>
                <motion.a
                  href="/contact"
                  style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none', fontSize: '0.95rem' }}
                  whileHover={{ color: colors.primary, x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Contact
                </motion.a>
              </li>
              <li style={{ marginBottom: '12px' }}>
                <motion.a
                  href="https://anydesk.com/fr/downloads"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none', fontSize: '0.95rem', display: 'flex', alignItems: 'center'
                  }}
                  whileHover={{ color: colors.primary, x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaDownload style={{ marginRight: '8px', fontSize: '0.9rem' }} />
                  Télécharger AnyDesk
                </motion.a>
              </li>
              <li style={{ marginBottom: '12px' }}>
                <motion.a
                  href="/mentions-legales"
                  style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none', fontSize: '0.95rem' }}
                  whileHover={{ color: colors.primary, x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Mentions légales
                </motion.a>
              </li>
              <li>
                <motion.a
                  href="/privacy"
                  style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none', fontSize: '0.95rem' }}
                  whileHover={{ color: colors.primary, x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  Politique de confidentialité
                </motion.a>
              </li>
            </ul>
          </div>
        </motion.div>

        <motion.div
          style={{
            marginTop: '50px',
            paddingTop: '30px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px'
          }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <p style={{
            margin: 0,
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.9rem',
            fontWeight: '500',
          }}
          >
            &copy; {new Date().getFullYear()} Drivesoft. Tous droits réservés. Made by <a href="https://brxdigital.com" style={{ color: colors.primary, textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">brxdigital.com</a>
          </p>
          <div>
            <motion.a
              href="/mentions-legales"
              style={linkStyle}
              whileHover={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: colors.primary
              }}
            >
              Mentions légales
            </motion.a>
            <motion.a
              href="/privacy"
              style={linkStyle}
              whileHover={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: colors.primary
              }}
            >
              Politique de confidentialité
            </motion.a>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}

export default Footer;
